import React, {useEffect, useState} from "react";
import {Grid, Paper, FormControl, TextField, InputAdornment, IconButton, Link} from "@material-ui/core"
import _ from "lodash";
import {useField, useFieldWithValidation} from "../../../../../hooks";
import InputComboBox from "../../../sharedFormComponents/InputComboBox";
import {familyOfficeTypes} from "../../InvestorConstants";
import {validEmail} from "../../../../../helpers/utils";
import useYears from "../../../../../hooks/useYears";
import SingleAsyncAutoComplete from "../../../shared/SingleAsyncAutoComplete";
import useDataFetchers from "../../../../../hooks/useDataFetchers";
import SingleAutoComplete from "../../../shared/SingleAutoComplete";
import LinkIcon from '@material-ui/icons/Link';
import UrlTextField from "../../../shared/Inputs/UrlTextField";
import {CurrencyFormatter} from "../../../shared/Formatters";

const GeneralSection = (
    {
        investor, isEditing,
        handleOnChange, handleOnChangeInField,
        handleOnError, session, aums,
        strategyFocuses, investorTypes, drafts,
        ...props
    }
) => {
    const {reset: resetName, validate: validateName, errorMessage: nameErrorMessage, ...name} = useFieldWithValidation(
        "text",
        investor.name || "",
        (value) => (!_.isEmpty(value)),
        true,
        ({target:{ name, value }}) => handleOnChangeInField(name, value)
    );
    const {reset: resetWebsite, validate: validateWebsite, errorMessage: websiteErrorMessage, ...website} = useFieldWithValidation(
        "text",
        investor.website || "",
        () => true,
        false,
        ({target:{ name, value }}) => handleOnChangeInField(name, value)
    );
    const {reset: resetPhone, validate: validatePhone, errorMessage: phoneErrorMessage, ...phone} = useFieldWithValidation(
        "text",
        investor.phone || "",
        () => true,
        false,
        ({target:{ name, value }}) => handleOnChangeInField(name, value)
    );
    const {reset: resetCompanyEmail, validate: validateCompanyEmail, errorMessage: companyEmailErrorMessage, ...company_email} = useFieldWithValidation(
        "text",
        investor.company_email || "",
        (value) => (_.isEmpty(value) || validEmail(value)),
        false,
        ({target:{ name, value }}) => handleOnChangeInField(name, value)
    );
    const {reset: resetCrdId, ...crd_id} = useField(
        'text',
        false,
        investor.crd_id || "",
        ({target:{ name, value }}) => handleOnChangeInField(name, value)
    );
    const {reset: resetSecId, ...sec_id} = useField(
        'text',
        false,
        investor.sec_id || "",
        ({target:{ name, value }}) => handleOnChangeInField(name, value)
    );
    // const [selectedStrategyFocuses, setSelectedStrategyFocuses] = useState(investor.strategy_ids?.map((strategy_id) => strategyFocuses.find(({id}) => id === strategy_id)) || []);
    const fetchers = useDataFetchers(session);

    const requiredErrors = () => {
        return name.error || _.isEmpty(investor.family_office_type)
    }

    useEffect(() => {
        handleOnError(requiredErrors());
    }, [
        name.error, investor.family_office_type
    ]);

    const years = useYears(true);

    const handleResearcherChange = (researcher) => {
        handleOnChangeInField('researcher_id', researcher?.id);
        handleOnChangeInField('researcher_name', researcher?.name);
    }

    /*
    This is not used anymore
    const handleStrategyFocusChange = (strategyFocuses) => {
        setSelectedStrategyFocuses(strategyFocuses)
        handleOnChangeInField('strategy_ids', strategyFocuses.map(({id}) => id))
    }
    */

    return (
        <Grid container item xs={12}>
            <Paper className="form-container" elevation={3}>
                <Grid item xs={12}>
                    <h3>General</h3>
                </Grid>
                <Grid container spacing={3}>
                    <Grid item xs={4}>
                        <FormControl fullWidth>
                            <TextField
                                variant="outlined"
                                {...name}
                                label={"Name"}
                                name={"name"}
                                maxLength={40}
                            />
                        </FormControl>
                    </Grid>

                    <Grid item xs={4}>
                        <SingleAutoComplete
                            options={investorTypes}
                            handleOnChange={(value) => handleOnChangeInField('investor_type_id', value?.id || null)}
                            handleOnError={() => {}}
                            selectedId={investor.investor_type_id}
                            field={'investor_type_id'}
                            label={"Investor Type"}
                            variant={"outlined"}
                            className="entity-autocomplete"
                            autoHighlight
                        />
                    </Grid>

                    <Grid item xs={2}>
                        <SingleAutoComplete
                            options={familyOfficeTypes}
                            handleOnChange={(value) => handleOnChangeInField('family_office_type', value?.id || null)}
                            handleOnError={() => _.isEmpty(investor.family_office_type)}
                            selectedId={investor.family_office_type}
                            field={'family_office_type'}
                            label={"Family Office Type"}
                            variant={"outlined"}
                            className="entity-autocomplete"
                            autoHighlight
                            required
                        />
                    </Grid>

                    <Grid item xs={2}>
                        <SingleAutoComplete
                            options={aums}
                            handleOnChange={(value) => handleOnChangeInField('aum_range_id', value?.id || null)}
                            handleOnError={() => {}}
                            selectedId={investor.aum_range_id}
                            field={'aum_range_id'}
                            label={"AUM"}
                            variant={"outlined"}
                            className="entity-autocomplete"
                            autoHighlight
                        />
                    </Grid>

                    <Grid item xs={2}>
                        <FormControl fullWidth>
                            <TextField
                                variant="outlined"
                                {...company_email}
                                label={"Company Email"}
                                name={"company_email"}
                                maxLength={40}
                            />
                        </FormControl>
                    </Grid>

                    <Grid item xs={3}>
                        <SingleAsyncAutoComplete
                            fetch={fetchers.researchers}
                            handleOnChange={(value) => handleResearcherChange(value)}
                            value={{id: investor?.researcher_id, name: investor?.researcher_name || ''}}
                            field={'researcher_id'}
                            label={"Researcher"}
                        />
                    </Grid>

                    <Grid item xs={3}>
                        <FormControl fullWidth>
                            <UrlTextField
                                variant="outlined"
                                {...website}
                                label={"Website"}
                                name={"website"}
                                maxLength={150}
                            />
                        </FormControl>
                    </Grid>

                    <Grid item xs={2}>
                        <FormControl fullWidth>
                            <TextField
                                variant="outlined"
                                {...phone}
                                label={"Phone"}
                                name={"phone"}
                                maxLength={40}
                            />
                        </FormControl>
                    </Grid>

                    <Grid item xs={2}>
                        <SingleAutoComplete
                            options={years}
                            handleOnChange={(value) => handleOnChangeInField('founded_in', value?.id || null)}
                            handleOnError={() => {}}
                            selectedId={investor?.founded_in?.toString() || ""}
                            field={'founded_in'}
                            label={"Founded In"}
                            variant={"outlined"}
                        />
                    </Grid>

                    <Grid item xs={4}>
                        <FormControl fullWidth>
                            <TextField
                                label='Net Worth'
                                variant='outlined'
                                name='family_net_worth'
                                value={investor?.family_net_worth || ''}
                                onChange={({target:{ name, value }}) => handleOnChangeInField(name, value)}
                                InputLabelProps={{ shrink: investor?.family_net_worth !== ''}}
                                InputProps={{
                                    inputComponent: CurrencyFormatter
                                }}
                            />
                        </FormControl>
                    </Grid>

                    {
                        !drafts && <>
                            <Grid item xs={4}>
                                <FormControl fullWidth>
                                    <TextField
                                        variant="outlined"
                                        {...crd_id}
                                        label={"CRD"}
                                        name={"crd_id"}
                                        type="number"
                                        maxLength={40}
                                        disabled={!(session.isSuperAdmin || session.inHouseResearch)}
                                    />
                                </FormControl>
                            </Grid>

                            <Grid item xs={4}>
                                <FormControl fullWidth>
                                    <TextField
                                        variant="outlined"
                                        {...sec_id}
                                        label={"SEC"}
                                        name={"sec_id"}
                                        maxLength={40}
                                        disabled={!(session.isSuperAdmin || session.inHouseResearch)}
                                    />
                                </FormControl>
                            </Grid>
                        </>
                    }
                </Grid>
            </Paper>
        </Grid>
    )
}

export default GeneralSection;